import React, { useEffect, useState } from "react";
import { useOutletContext, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Button, Card, Pill, Spinner } from "@/components/ui-kit";
import MathText from "@/components/MathText";
import { Lightbulb, AlertCircle, Dumbbell, MessagesSquare, ArrowRight, Sparkles } from "lucide-react";
import { gradeShort } from "@/config";
import NumberLine, { isCountingSkill } from "@/components/NumberLine";

export default function Lesson() {
  const { studentId } = useOutletContext();
  const { skillId } = useParams();
  const nav = useNavigate();
  const [skill, setSkill] = useState(null);

  useEffect(() => { api.get(`/skills/${skillId}`).then(({ data }) => setSkill(data)); }, [skillId]);
  if (!skill) return <Spinner label="Opening lesson" />;

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
      <div className="flex items-center gap-2 mb-2">
        <Pill tone="primary">{gradeShort(skill.grade)} · {skill.strand}</Pill>
      </div>
      <h1 className="font-head text-3xl font-bold mb-2">{skill.name}</h1>

      <Card className="p-5 mb-4 border-l-4" style={{ borderLeftColor: "var(--ns-primary)" }} data-testid="why-matters">
        <div className="text-xs font-semibold uppercase tracking-wider text-[var(--ns-subtle)] mb-1">Why this matters next</div>
        <div className="text-[var(--ns-text)]"><MathText text={skill.why_it_matters} /></div>
      </Card>

      <Card className="p-5 mb-4" data-testid="lesson-body">
        <div className="text-base leading-relaxed text-[var(--ns-text)]"><MathText text={skill.lesson} /></div>
        <div className="mt-4 grid gap-2">
          {skill.examples.map((ex, i) => (
            <div key={i} className="rounded-[var(--ns-radius)] bg-[var(--ns-surface-subtle)] px-4 py-3 flex items-center gap-2">
              <Lightbulb size={16} className="text-[var(--ns-gold)] shrink-0" />
              <MathText text={ex} />
            </div>
          ))}
        </div>
      </Card>

      {skill.misconceptions?.length > 0 && (
        <Card className="p-5 mb-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-[var(--ns-subtle)] mb-2">Watch out for</div>
          <ul className="grid gap-2">
            {skill.misconceptions.map((m, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-[var(--ns-muted)]"><AlertCircle size={16} className="text-[#D96B43] mt-0.5 shrink-0" /> {m}</li>
            ))}
          </ul>
        </Card>
      )}

      <Card className="p-4 mb-4 flex items-center gap-2 text-sm">
        <Sparkles size={18} className="text-[var(--ns-gold)]" /> <span className="text-[var(--ns-muted)]"><b className="text-[var(--ns-text)]">To master:</b> {skill.mastery_criteria}</span>
      </Card>

      {isCountingSkill(skillId) && (
        <Card className="p-4 mb-4" data-testid="lesson-number-line">
          <div className="text-xs font-semibold uppercase tracking-wider text-[var(--ns-subtle)] mb-2">Try it — tap to count</div>
          <NumberLine max={20} />
        </Card>
      )}

      <div className="grid sm:grid-cols-3 gap-3">
        <Button data-testid="lesson-practice-btn" onClick={() => nav(`/student/${studentId}/practice/${skillId}`)}><Dumbbell size={16} /> Practice</Button>
        <Button variant="outline" data-testid="lesson-mastery-btn" onClick={() => nav(`/student/${studentId}/mastery/${skillId}`)}>Mastery check <ArrowRight size={16} /></Button>
        <Button variant="ghost" data-testid="lesson-tutor-btn" onClick={() => nav(`/student/${studentId}/tutor/${skillId}`)}><MessagesSquare size={16} /> Ask tutor</Button>
      </div>
    </motion.div>
  );
}
